import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { api, formatApiError, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/lib/seo";
import WebsiteEditor from "@/components/admin/WebsiteEditor";

const FUNNEL_STAGES = [
  ["visitors", "Visitors"], ["leads", "Leads"], ["trial_signups", "Trial Starts"],
  ["activated_trials", "Activated Trials"], ["checkout_started", "Checkout"], ["paid_customers", "Paid Customers"],
];

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
      <p className="text-xs text-slate-500 font-medium">{label}</p>
      <p className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</p>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [leads, setLeads] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [forbidden, setForbidden] = useState(false);
  const [adminSubs, setAdminSubs] = useState([]);

  useSEO({ title: "Sales Dashboard — Mashara Finance Admin", description: "Admin dashboard.", path: "/admin", noindex: true });

  const loadLeads = useCallback(() => {
    api.get("/admin/leads").then((r) => { setLeads(r.data.leads); setStatuses(r.data.statuses); })
      .catch((e) => { if (e.response?.status === 403) setForbidden(true); });
  }, []);

  useEffect(() => {
    if (user === null) { navigate("/admin/login"); return; }
    if (!user) return;
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch((e) => { if (e.response?.status === 403) setForbidden(true); });
    loadLeads();
    api.get("/admin/subscriptions").then((r) => setAdminSubs(r.data.subscriptions || [])).catch(() => {});
  }, [user, navigate, loadLeads]);

  if (user && user.role !== "admin" && forbidden) {
    return <div className="py-32 text-center" data-testid="admin-forbidden"><h1 className="font-display text-2xl font-bold text-slate-900">Admin access required</h1></div>;
  }
  if (!overview) return <div className="py-32 text-center text-slate-500">Loading dashboard…</div>;

  const f = overview.funnel;
  const pct = (a, b) => (b > 0 ? `${Math.round((a / b) * 100)}%` : "—");

  const updateLeadStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success("Lead updated");
      loadLeads();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-dashboard" className="py-10 bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Sales & Marketing Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1">Funnel, leads pipeline, subscriptions and full website editor.</p>
          </div>
          <Button variant="outline" data-testid="admin-logout-button" onClick={async () => { await logout(); navigate("/"); }} className="rounded-xl border-slate-300">Sign out</Button>
        </div>

        <Tabs defaultValue="analytics">
          <TabsList data-testid="admin-tabs" className="mb-6">
            <TabsTrigger value="analytics" data-testid="admin-tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="leads" data-testid="admin-tab-leads">Leads Pipeline</TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="admin-tab-subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="cms" data-testid="admin-tab-cms">Website Editor</TabsTrigger>
          </TabsList>

          <TabsContent value="analytics">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Stat label="Monthly Recurring Revenue" value={formatINR(overview.mrr)} testid="stat-mrr" />
              <Stat label="Annual Recurring Revenue" value={formatINR(overview.arr)} testid="stat-arr" />
              <Stat label="Visitor → Lead" value={`${overview.visitor_to_lead_rate}%`} testid="stat-visitor-lead" />
              <Stat label="Trial → Paid" value={`${overview.trial_to_paid_rate}%`} testid="stat-trial-paid" />
            </div>
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="funnel-panel">
                <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Revenue Funnel</h2>
                <div className="space-y-2">
                  {FUNNEL_STAGES.map(([key, label], i) => (
                    <div key={key}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">{label}</span>
                        <span className="font-bold text-slate-900" data-testid={`funnel-${key}`}>{f[key]}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full mt-1">
                        <div className="h-2 bg-emerald-600 rounded-full" style={{ width: `${f.visitors ? Math.max(3, (f[key] / f.visitors) * 100) : 0}%` }} />
                      </div>
                      {i > 0 && <p className="text-[10px] text-slate-400 mt-0.5">conversion from previous: {pct(f[key], f[FUNNEL_STAGES[i - 1][0]])}</p>}
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="traffic-chart">
                <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Page Views (30 days)</h2>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={overview.daily_views.map(([date, views]) => ({ date: date.slice(5), views }))}>
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Area type="monotone" dataKey="views" stroke="#047857" fill="#047857" fillOpacity={0.15} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="top-sources">
                <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Top Traffic Sources</h2>
                {overview.top_sources.length === 0 ? <p className="text-sm text-slate-500">No UTM-tracked visits yet.</p> : (
                  <ul className="space-y-2">{overview.top_sources.map(([s, c]) => (
                    <li key={s} className="flex justify-between text-sm"><span className="text-slate-700">{s}</span><span className="font-semibold">{c}</span></li>
                  ))}</ul>
                )}
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="top-pages">
                <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Top Landing Pages</h2>
                <ul className="space-y-2">{overview.top_pages.map(([p, c]) => (
                  <li key={p} className="flex justify-between text-sm"><span className="text-slate-700">{p}</span><span className="font-semibold">{c}</span></li>
                ))}</ul>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="leads">
            <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="leads-panel">
              <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Lead Pipeline ({leads.length})</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                      <th className="pb-3 pr-4">Lead</th><th className="pb-3 pr-4">Contact</th><th className="pb-3 pr-4">Type</th>
                      <th className="pb-3 pr-4">Source</th><th className="pb-3 pr-4">Status</th><th className="pb-3">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l) => (
                      <tr key={l.id} className="border-b border-slate-100" data-testid={`lead-row-${l.id}`}>
                        <td className="py-3 pr-4">
                          <p className="font-semibold text-slate-900">{l.name}</p>
                          <p className="text-xs text-slate-500">{l.organization || "—"}{l.business_type ? ` · ${l.business_type}` : ""}</p>
                        </td>
                        <td className="py-3 pr-4 text-xs text-slate-600">{l.email}<br />{l.mobile}</td>
                        <td className="py-3 pr-4"><span className="text-xs bg-slate-100 rounded-full px-2 py-1 font-medium">{l.lead_type}</span></td>
                        <td className="py-3 pr-4 text-xs text-slate-600">{l.utm?.utm_source || l.source || "direct"}</td>
                        <td className="py-3 pr-4">
                          <Select value={l.status} onValueChange={(v) => updateLeadStatus(l.id, v)}>
                            <SelectTrigger data-testid={`lead-status-${l.id}`} className="h-8 text-xs w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {statuses.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-3 text-xs text-slate-500">{(l.created_at || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {leads.length === 0 && <p className="text-sm text-slate-500 py-8 text-center">No leads yet. Share your site to start capturing leads.</p>}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="subscriptions">
            <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="admin-subscriptions-panel">
              <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Organization Subscriptions ({adminSubs.length})</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                      <th className="pb-3 pr-4">Organization</th><th className="pb-3 pr-4">Plan</th><th className="pb-3 pr-4">Status</th>
                      <th className="pb-3 pr-4">Usage</th><th className="pb-3 pr-4">Amount</th><th className="pb-3 pr-4">Cycle</th>
                      <th className="pb-3 pr-4">Razorpay Sub ID</th><th className="pb-3 pr-4">Next Billing</th><th className="pb-3">Trial Ends</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminSubs.map((s) => (
                      <tr key={s.org_id} className="border-b border-slate-100" data-testid={`admin-sub-${s.org_id}`}>
                        <td className="py-3 pr-4 font-semibold text-slate-900">{s.organization}</td>
                        <td className="py-3 pr-4">{s.plan}</td>
                        <td className="py-3 pr-4">
                          <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${s.status === "active" || s.status === "trialing" ? "bg-emerald-100 text-emerald-800" : s.status === "payment_pending" || s.status === "past_due" || s.status === "pending_verification" ? "bg-amber-100 text-amber-800" : "bg-slate-200 text-slate-600"}`}>{s.status}</span>
                        </td>
                        <td className="py-3 pr-4 text-xs text-slate-600">{s.users_used}/{s.user_limit ?? "∞"} users · {s.centers_used}/{s.center_limit ?? "∞"} centers</td>
                        <td className="py-3 pr-4">{s.amount ? formatINR(s.amount / 100) : "—"}</td>
                        <td className="py-3 pr-4 capitalize text-xs">{s.billing_cycle || "—"}</td>
                        <td className="py-3 pr-4 text-xs font-mono text-slate-500">{s.razorpay_subscription_id || "—"}</td>
                        <td className="py-3 pr-4 text-xs text-slate-600">{(s.next_billing_date || "").slice(0, 10) || "—"}</td>
                        <td className="py-3 text-xs text-slate-600">{(s.trial_ends_at || "").slice(0, 10) || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {adminSubs.length === 0 && <p className="text-sm text-slate-500 py-8 text-center">No organization subscriptions yet.</p>}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="cms">
            <WebsiteEditor />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
